import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import CircularProgress from '@mui/material/CircularProgress';
import CloseIcon from '@mui/icons-material/Close';
import FilterIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import { CallSearchRequest } from '../../types/Call';
import { FilterValues } from '../../types/Call';

interface FilterPaneProps {
  open: boolean;
  onClose: () => void;
  filters: Partial<CallSearchRequest>;
  /** Called once, on Apply — commits the full local filter set to the parent */
  onFiltersChange: (filters: Partial<CallSearchRequest>) => void;
  onClearFilters: () => void;
  /** Dynamic filter options from the backend */
  filterOptions?: FilterValues;
  /** True while filter values are loading */
  optionsLoading?: boolean;
  /** Offers the CAD report filter; false while the agency has CAD records off */
  cadEnabled?: boolean;
}

export const FilterPane: React.FC<FilterPaneProps> = ({
  open,
  onClose,
  filters,
  onFiltersChange,
  onClearFilters,
  filterOptions,
  optionsLoading,
  cadEnabled = false,
}) => {
  const [localFilters, setLocalFilters] = useState(filters);

  // Keep local edits in sync with the committed filters — and reset any unapplied
  // edits whenever the pane (re)opens or the parent resets (e.g. Clear All from KPI bar).
  useEffect(() => {
    setLocalFilters(filters);
  }, [filters, open]);

  // Edits stay local until Apply — they must not trigger a fetch on every keystroke/selection.
  const handleFilterChange = (key: keyof CallSearchRequest, value: CallSearchRequest[keyof CallSearchRequest]) => {
    setLocalFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleApply = () => {
    onFiltersChange(localFilters);
    onClose();
  };

  const handleClear = () => {
    setLocalFilters({});
    onClearFilters();
  };

  const getActiveFiltersCount = () => {
    return Object.values(localFilters).filter(value => 
      value !== undefined && value !== null && value !== ''
    ).length;
  };

  const drawerContent = (
    <Box sx={{ width: 320, p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center">
          <FilterIcon sx={{ mr: 1 }} />
          <Typography variant="h6">Filters</Typography>
          {getActiveFiltersCount() > 0 && (
            <Chip
              label={getActiveFiltersCount()}
              size="small"
              color="primary"
              sx={{ ml: 1 }}
            />
          )}
        </Box>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Stack spacing={3}>
        {/* Date Range */}
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Date Range
          </Typography>
          <TextField
            fullWidth
            label="Start Date"
            type="date"
            value={localFilters.startDate || ''}
            onChange={(e) => handleFilterChange('startDate', e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="End Date"
            type="date"
            value={localFilters.endDate || ''}
            onChange={(e) => handleFilterChange('endDate', e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Box>

        <Divider />

        {/* Call Type — dynamic values */}
        <FormControl fullWidth>
          <InputLabel>Call Type</InputLabel>
          <Select
            value={localFilters.callType || ''}
            label="Call Type"
            onChange={(e) => handleFilterChange('callType', e.target.value)}
          >
            <MenuItem value="">All Types</MenuItem>
            {optionsLoading && (
              <MenuItem disabled>
                <CircularProgress size={16} sx={{ mr: 1 }} /> Loading…
              </MenuItem>
            )}
            {filterOptions?.callTypes.map((ct) => (
              <MenuItem key={ct} value={ct}>{ct}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Operator Name — dynamic values (replaces Status) */}
        <FormControl fullWidth>
          <InputLabel>Operator Name</InputLabel>
          <Select
            value={localFilters.operator || ''}
            label="Operator Name"
            onChange={(e) => handleFilterChange('operator', e.target.value)}
          >
            <MenuItem value="">All Operators</MenuItem>
            {optionsLoading && (
              <MenuItem disabled>
                <CircularProgress size={16} sx={{ mr: 1 }} /> Loading…
              </MenuItem>
            )}
            {filterOptions?.operators.map((op) => (
              <MenuItem key={op} value={op}>{op}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Review Status — dynamic values */}
        <FormControl fullWidth>
          <InputLabel>Review Status</InputLabel>
          <Select
            value={localFilters.reviewStatus || ''}
            label="Review Status"
            onChange={(e) => handleFilterChange('reviewStatus', e.target.value)}
          >
            <MenuItem value="">All Review Statuses</MenuItem>
            {optionsLoading && (
              <MenuItem disabled>
                <CircularProgress size={16} sx={{ mr: 1 }} /> Loading…
              </MenuItem>
            )}
            {filterOptions?.reviewStatuses.map((rs) => (
              <MenuItem key={rs} value={rs}>{rs}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* CAD report: which calls still need one */}
        {cadEnabled && (
          <FormControl fullWidth>
            <InputLabel>CAD Report</InputLabel>
            <Select
              value={localFilters.cadReport || ''}
              label="CAD Report"
              onChange={(e) => handleFilterChange('cadReport', e.target.value || undefined)}
            >
              <MenuItem value="">All Calls</MenuItem>
              <MenuItem value="Missing">Missing a CAD report</MenuItem>
              <MenuItem value="Attached">Has a CAD report</MenuItem>
            </Select>
          </FormControl>
        )}

        {/* Score Range */}
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Overall Score Range
          </Typography>
          <TextField
            fullWidth
            label="Min Score"
            type="number"
            value={localFilters.minQualityScore || ''}
            onChange={(e) => handleFilterChange('minQualityScore', e.target.value ? Number(e.target.value) : undefined)}
            inputProps={{ min: 0, max: 100, step: 1 }}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Max Score"
            type="number"
            value={localFilters.maxQualityScore || ''}
            onChange={(e) => handleFilterChange('maxQualityScore', e.target.value ? Number(e.target.value) : undefined)}
            inputProps={{ min: 0, max: 100, step: 1 }}
          />
        </Box>

        <Divider />

        {/* Action Buttons */}
        <Stack spacing={2}>
          <Button
            variant="contained"
            onClick={handleApply}
            fullWidth
          >
            Apply Filters
          </Button>
          <Button
            variant="outlined"
            onClick={handleClear}
            startIcon={<ClearIcon />}
            fullWidth
          >
            Clear All
          </Button>
        </Stack>
      </Stack>
    </Box>
  );

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      variant="temporary"
      sx={{
        '& .MuiDrawer-paper': {
          boxSizing: 'border-box',
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
};